import { useState } from 'react';
import { Search, Filter, FileText, Calendar, User, Building2, Package, Bell, MessageCircle, X } from 'lucide-react';

function SearchPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Mock search data
  const mockData = {
    appointments: [
      { id: 1, title: 'Client Meeting - John Smith', type: 'appointment', category: 'Appointments', date: '2024-01-20', description: 'Follow-up meeting with client regarding case #12345' },
      { id: 2, title: 'Training Session - Safety Protocol', type: 'appointment', category: 'Appointments', date: '2024-01-22', description: 'Mandatory safety training for all officers' }
    ],
    orders: [
      { id: 3, title: 'Equipment Order - Patrol Vehicles', type: 'order', category: 'Orders', date: '2024-01-18', description: 'Purchase order for 5 new patrol vehicles' },
      { id: 4, title: 'Supply Order - Office Supplies', type: 'order', category: 'Orders', date: '2024-01-19', description: 'Monthly office supplies replenishment' }
    ],
    forms: [
      { id: 5, title: 'Incident Report Form', type: 'form', category: 'Forms', date: '2024-01-15', description: 'Standard form for reporting incidents and accidents' },
      { id: 6, title: 'Leave Application Form', type: 'form', category: 'Forms', date: '2024-01-14', description: 'HR form for leave requests and approvals' }
    ],
    circulars: [
      { id: 7, title: 'New Policy Implementation', type: 'circular', category: 'Circulars', date: '2024-01-16', description: 'Updated policy regarding overtime procedures' },
      { id: 8, title: 'Holiday Schedule Update', type: 'circular', category: 'Circulars', date: '2024-01-17', description: 'Revised holiday schedule for Q1 2024' }
    ],
    users: [
      { id: 9, title: 'John Doe - Senior Officer', type: 'user', category: 'Users', date: '2024-01-10', description: 'Senior patrol officer with 8 years experience' },
      { id: 10, title: 'Jane Smith - Admin Staff', type: 'user', category: 'Users', date: '2024-01-12', description: 'Administrative staff member' }
    ],
    companies: [
      { id: 11, title: 'ABC Security Services', type: 'company', category: 'Companies', date: '2024-01-08', description: 'External security contractor for special events' },
      { id: 12, title: 'XYZ Equipment Co.', type: 'company', category: 'Companies', date: '2024-01-09', description: 'Supplier of law enforcement equipment' }
    ]
  };

  const categories = [
    { key: 'all', label: 'All Categories', icon: Search },
    { key: 'appointments', label: 'Appointments', icon: Calendar },
    { key: 'orders', label: 'Orders', icon: Package },
    { key: 'forms', label: 'Forms', icon: FileText },
    { key: 'circulars', label: 'Circulars', icon: FileText },
    { key: 'users', label: 'Users', icon: User },
    { key: 'companies', label: 'Companies', icon: Building2 }
  ];

  const performSearch = () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    // Simulate search delay
    setTimeout(() => {
      const results = [];
      const searchLower = searchTerm.toLowerCase();
      
      Object.entries(mockData).forEach(([category, items]) => {
        if (selectedCategory === 'all' || selectedCategory === category) {
          items.forEach(item => {
            if (item.title.toLowerCase().includes(searchLower) ||
                item.description.toLowerCase().includes(searchLower)) {
              results.push(item);
            }
          });
        }
      });
      
      setSearchResults(results);
      setIsSearching(false);
    }, 500);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch();
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Appointments':
        return <Calendar className="h-4 w-4" />;
      case 'Orders':
        return <Package className="h-4 w-4" />;
      case 'Forms':
        return <FileText className="h-4 w-4" />;
      case 'Circulars':
        return <FileText className="h-4 w-4" />;
      case 'Users':
        return <User className="h-4 w-4" />;
      case 'Companies':
        return <Building2 className="h-4 w-4" />;
      default:
        return <Search className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Appointments':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'Orders':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'Forms':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'Circulars':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'Users':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
      case 'Companies':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Global Search</h1>
        <p className="text-lg text-gray-500 dark:text-gray-400">
          Search across all modules and data in the system
        </p>
      </div>

      {/* Search Form */}
      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
            <input
              type="text"
              placeholder="Search for appointments, orders, forms, users, companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-20 py-4 text-lg border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent shadow-lg"
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
              <button
                type="submit"
                disabled={!searchTerm.trim() || isSearching}
                className="px-6 py-2 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center justify-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Filter by:</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category.key} value={category.key}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </form>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Search Results ({searchResults.length})
            </h2>
            <button
              onClick={clearSearch}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
            >
              Clear Results
            </button>
          </div>

          <div className="space-y-4">
            {searchResults.map((result) => (
              <div
                key={result.id}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow duration-200 cursor-pointer"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    {getCategoryIcon(result.category)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {result.title}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(result.category)}`}>
                        {result.category}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
                      {result.description}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <span>Date: {result.date}</span>
                      <span>Type: {result.type}</span>
                      <span>ID: {result.id}</span>
                    </div>
                  </div>
                  
                  <button className="px-3 py-2 text-sm font-medium text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors duration-200">
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {searchTerm && searchResults.length === 0 && !isSearching && (
        <div className="text-center py-12">
          <Search className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No results found</h3>
          <p className="text-gray-500 dark:text-gray-400">
            Try adjusting your search terms or category filter.
          </p>
        </div>
      )}

      {/* Search Suggestions */}
      {!searchTerm && (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Popular Searches</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.slice(1).map((category) => (
              <button
                key={category.key}
                onClick={() => {
                  setSelectedCategory(category.key);
                  setSearchTerm(category.label.split(' ')[0]);
                }}
                className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200 text-center"
              >
                <category.icon className="mx-auto h-8 w-8 text-brand-primary mb-2" />
                <p className="text-sm font-medium text-gray-900 dark:text-white">{category.label}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchPage;
